import { CommonModule } from '@angular/common';
import { Component, computed, inject, Inject, Optional, signal, Signal, WritableSignal } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InputDropdownConfig } from '@app/_interfaces/generic.interface';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { SettingsService } from '@app/_services/settings.service';
import { ViewSettingService } from '@app/_services/view-setting.service';
import { FeatureStore, ViewSettingsStore } from '@app/_stores';
import { ShowType } from '@app/_types/generic.type';
import { KebabShowMenuComponent } from '@app/components/shared/kebab-show-menu/kebab-show-menu.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { InputDropdownComponent } from '@app/components/shared/inputs/input-dropdown/input-dropdown.component';
import { InputToggleComponent } from '@app/components/shared/inputs/input-toggle/input-toggle.component';
import { CacheService } from '@app/_services/cache.service';

@Component({
  selector: 'app-entity-past-future-view',
  imports: [
    CommonModule,
    SvgComponent,
    KebabShowMenuComponent,
    InputDropdownComponent,
    InputToggleComponent
  ],
  templateUrl: './entity-past-future-view.component.html',
  styleUrl: './entity-past-future-view.component.scss'
})
export class EntityPastFutureViewComponent {

  readonly featureStore = inject(FeatureStore);
  readonly viewSettings = inject(ViewSettingsStore);
  mode: Signal<'PAST' | 'FUTURE'> = computed(() => {
    return this.data.mode;
  });
  hiddenValues: Signal<ShowType[]> = computed(() => {
    const hiddenValues: ShowType[] = ['doneDate', 'date', 'label'];
    const activeEntities = this.featureStore.activeEntities();
    const mode = this.mode();

    // invalidEntry is only for Past (Habit and Journal), not Future
    // if (mode === 'FUTURE') {
    //   hiddenValues.push('invalidEntry');
    // }

    if (!activeEntities.includes('habit')) {
      hiddenValues.push('habitResponse');
    }
    if (!activeEntities.includes('todo')) {
      hiddenValues.push('checklist');
    }
    if (!activeEntities.includes('calendarIntegration')) {
      hiddenValues.push('calendarName');
    }
    if (!activeEntities.includes('journal') && !activeEntities.includes('note')) {
      hiddenValues.push('mood');
    }
    if (!activeEntities.includes('moneyTracker')) {
      hiddenValues.push('setup');
    }
    return hiddenValues;
  });
  entityShowConfig: Signal<{ [key: string]: InputDropdownConfig }> = computed(() => {
    return this.vs.entityShowConfig();
  });

  // Get writable signal reference based on mode
  showSignal: Signal<WritableSignal<ShowType[]>> = computed(() => {
    const mode = this.mode();
    return this.ss.viewSignals()[mode === 'PAST' ? 'pastShow' : 'futureShow'];
  });

  // Group by signal - always 'date' for past/future but needed for UI consistency
  groupBySignal: Signal<WritableSignal<string>> = computed(() => {
    const mode = this.mode();
    return this.ss.viewSignals()[mode === 'PAST' ? 'pastTodoGroupBy' : 'futureTodoGroupBy'];
  });

  // Grouped view type signal for collapsed view and show counts
  groupedViewTypeSignal: Signal<WritableSignal<string[]>> = computed(() => {
    const mode = this.mode();
    return this.ss.viewSignals()[mode === 'PAST' ? 'pastTodoGroupedViewType' : 'futureTodoGroupedViewType'];
  });

  // Group by config - Past has date and hashtag options, Future is date only
  groupByConfig: Signal<{ [key: string]: InputDropdownConfig }> = computed(() => {
    const mode = this.mode();
    return mode === 'PAST' ? this.vs.pastGroupByConfig() : this.vs.dateOnlyGroupByConfig();
  });

  // Whether invalidEntry toggle should be visible (only when habit or journal is active)
  showInvalidEntryToggle: Signal<boolean> = computed(() => {
    const activeEntities = this.featureStore.activeEntities();
    return activeEntities.includes('habit') || activeEntities.includes('journal');
  });

  // Toggle signals for emptyDays and invalidEntry (derived from ShowType[] arrays)
  emptyDaysToggle: WritableSignal<boolean> = signal(false);
  invalidEntryToggle: WritableSignal<boolean> = signal(false);

  public data: { mode: 'PAST' | 'FUTURE' };

  constructor(
    public ss: SettingsService,
    @Optional() public dialogRef: MatDialogRef<EntityPastFutureViewComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: { mode: 'PAST' | 'FUTURE' },
    public vs: ViewSettingService,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<EntityPastFutureViewComponent>,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData: { mode: 'PAST' | 'FUTURE' },
    public cc: CacheService
  ) {
    this.data = (dialogData || bottomSheetData)!;

    // Initialize toggle signals from current show array values
    const showValues = this.showSignal()();
    this.emptyDaysToggle.set(showValues.includes('emptyDays'));
    this.invalidEntryToggle.set(showValues.includes('invalidEntry'));

    dialogRef?.backdropClick().subscribe(async () => {
      this.closeDialog();
    });

    bottomSheetRef?.backdropClick().subscribe(async () => {
      this.closeDialog();
    });
  }

  toggleEmptyDays() {
    const showSignal = this.showSignal();
    const current = showSignal();
    if (this.emptyDaysToggle()) {
      showSignal.set([...current, 'emptyDays']);
    } else {
      showSignal.set(current.filter((item: string) => item !== 'emptyDays'));
    }
    this.ss.updateView();
  }

  toggleInvalidEntry() {
    const showSignal = this.showSignal();
    const current = showSignal();
    if (this.invalidEntryToggle()) {
      showSignal.set([...current, 'invalidEntry']);
    } else {
      showSignal.set(current.filter((item: string) => item !== 'invalidEntry'));
    }
    this.ss.updateView();
  }

  closeDialog() {
    this.close();
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }
}
